import React, { useEffect, useState } from 'react'
import { isNullOrUndefined, isNullOrUndefinedOrBlankOrEmpty } from '../utils/generalFunctions'
import * as con from "../Constants"
import { setStoredValue } from '../store/actions'
import { RefAnnotationData } from '../hooks/AnnotationResultsHook'
import { Col, Row, Table, Tooltip, Typography, message } from 'antd'
import { BackButton, CustomGoButton, CustomDownloadButton } from '../elements/CustomButtons'
import { RefSetCurrentPage } from '../hooks/NavigationHooks'
import { RefLanguage } from '../hooks/LanguageHooks'
import { InfoCircleOutlined } from '@ant-design/icons'
import { createExcelExport } from '../utils/exportFunctions'
import { getDatabaseVersions } from '../utils/apiFunctions'
import { StateStoredValue } from '../hooks/StoreHooks'
import { formatEValue } from '../utils/chartFunctions'

const { Title } = Typography

// Mapping between frontend database constants and backend database IDs
const DATABASE_ID_MAPPING = {
    [con.DATABASE_ITS]: 'eukaryome_its',
    [con.DATABASE_SSU]: 'eukaryome_ssu', 
    [con.DATABASE_LSU]: 'eukaryome_lsu',
    [con.DATABASE_LrRNA]: 'eukaryome_long'
};

// Mapping between method constants and display names
const METHOD_DISPLAY_MAPPING = {
    [con.ANNOTATION_METHOD_BLAST]: 'BLASTn',
    [con.ANNOTATION_METHOD_MEGABLAST]: 'MegaBLAST',
    [con.ANNOTATION_METHOD_VSEARCH]: 'VSEARCH global',
    [con.ANNOTATION_METHOD_EXACT]: 'VSEARCH exact matches'
};

function AnnotationResultsPage() {


    // --------------------------

    // Translation
    const i18n = RefLanguage()

    // Data Hook
    const data = RefAnnotationData()

    // Table Data
    const [tableData, setTableData] = useState([])

    // Download state
    const [downloadLoading, setDownloadLoading] = useState(false)

    // Get selected database and method from store
    const [selectedDatabase] = StateStoredValue(con.SELECTED_DATABASE)
    const [selectedMethod] = StateStoredValue(con.SELECTED_METHOD)

    // Set the current page
    const setPage = RefSetCurrentPage()


    // Download handler
    const handleDownload = async () => {
        if (isNullOrUndefined(data) || data.length === 0) {
            message.warning(i18n.t("noDataToDownload") || "No data available to download");
            return;
        }

        setDownloadLoading(true);
        try {
            // For filename, generate current date in YYYYMMDD format
            const today = new Date();
            const dateString = today.getFullYear().toString() +
                (today.getMonth() + 1).toString().padStart(2, '0') +
                today.getDate().toString().padStart(2, '0');

            // Fetch database versions to get the version of selected database
            let databaseVersion = 'Unknown';
            let databaseDescription = selectedDatabase || 'Unknown';
            
            try {
                const dbVersionsResponse = await getDatabaseVersions();
                if (dbVersionsResponse && dbVersionsResponse.refdbs) {
                    const selectedDbId = DATABASE_ID_MAPPING[selectedDatabase];
                    const selectedDbInfo = dbVersionsResponse.refdbs.find(db => db.id === selectedDbId);
                    if (selectedDbInfo) {
                        databaseVersion = selectedDbInfo.version;
                        databaseDescription = selectedDbInfo.description;
                    }
                }
            } catch (error) {
                console.warn('Could not fetch database versions:', error);
                // Continue with unknown version rather than failing the download
            }

            const metadata = {
                'Database': databaseDescription,
                'Database version': databaseVersion,
                'Analysis method': METHOD_DISPLAY_MAPPING[selectedMethod] || selectedMethod || 'Unknown',
                'Analysis date': new Date().toISOString(),
                'Number of query sequences': data.length
            };

            const exportResult = createExcelExport(data, {
                filename: `EUKARYOME_annotation_results_${dateString}`,
                includeBestHits: true,
                includeLongFormat: true,
                includeWideFormat: true,
                includeLegend: true,
                metadata: metadata
            });

            // Create a link and trigger the download
            const url = URL.createObjectURL(exportResult.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = exportResult.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success(i18n.t("downloadSuccess") || "Results downloaded successfully");

        } catch (error) {
            console.error('Download error:', error);
            message.error(i18n.t("downloadError") || "Error downloading results");
        } finally {
            setDownloadLoading(false);
        }
    };

    useEffect(() => {

        if (isNullOrUndefined(data)) {
            setTableData([])
            return
        }

        // Has intermiedate data for selecting and manipulating the original data
        let newTableData = data.map((item) => {

            let newItem = {
                [con.ARC_QUERY_ID]: item[con.ARC_QUERY_ID],
            }

            // Sets the data from the first hit
            if (!isNullOrUndefinedOrBlankOrEmpty(item[con.ARC_HITS])) {
                let firstHit = item[con.ARC_HITS][0]
                newItem[con.ARC_PIDENT] = firstHit[con.ARC_PIDENT]         // Identity
                // newItem[con.ARC_BITSCORE] = firstHit[con.ARC_BITSCORE]  // Bit score
                newItem[con.ARC_QCOVS] = firstHit[con.ARC_QCOVS]           // Coverage
                newItem[con.ARC_EVALUE] = firstHit[con.ARC_EVALUE]         // E-value

                // Construct taxonomy string
                if (!isNullOrUndefined(firstHit[con.ARC_TAXONOMY])) {
                    const taxonomyObject = firstHit[con.ARC_TAXONOMY];
                    const taxonomyParts = con.TAXONOMIC_LEVELS.map(level => taxonomyObject[level]).filter(part => !isNullOrUndefined(part) && part !== "");
                    newItem[con.ARC_TAXONOMY_STRING] = taxonomyParts.join(" ❯ ");
                } else {
                    newItem[con.ARC_TAXONOMY_STRING] = con.MISSING_VALUE;
                }

            }

            return newItem
        })

        setTableData(newTableData)

    }, [data])

    // Defines the columns
    const GENERAL_COLUMNS = [
        {
            title: "Sequence ID",
            dataIndex: con.ARC_QUERY_ID,
            align: 'left',
            width: 120,
            render: (val) => !isNullOrUndefined(val) ? val : con.MISSING_VALUE
        },
        {
            title: (
                <Tooltip title={i18n.t("identityExplanation")}>
                    <span>{i18n.t("identity")}</span>
                </Tooltip>
            ),
            dataIndex: con.ARC_PIDENT,
            align: 'center',
            width: 60,
            render: (val, record) => !isNullOrUndefined(val) ? val : con.MISSING_VALUE

        },
        /*
        {
            title: (
                <Tooltip title={i18n.t("bitScoreExplanation")}>
                    <span>{i18n.t("bitScore")}</span>
                </Tooltip>
            ),
            dataIndex: con.ARC_BITSCORE,
            align: 'center',
            width: 80,
            render: (val, record) => !isNullOrUndefined(val) ? val : con.MISSING_VALUE

        },
        */
        {
            title: (
                <Tooltip title={i18n.t("coverageExplanation")}>
                    <span>{i18n.t("coverage")}</span>
                </Tooltip>
            ),
            dataIndex: con.ARC_QCOVS,
            align: 'center',
            width: 60,
            render: (val, record) => !isNullOrUndefined(val) ? val : con.MISSING_VALUE

        },
        {
            title: (
                <Tooltip title={i18n.t("eValueExplanation")}>
                    <span>{i18n.t("eValue")}</span>
                </Tooltip>
            ),
            dataIndex: con.ARC_EVALUE,
            align: 'center',
            width: 80,
            render: (val, record) => !isNullOrUndefined(val) ? formatEValue(val) : con.MISSING_VALUE

        },
        {
            title: (
                <Tooltip title={i18n.t("taxonomyExplanation")}>
                    <span>{i18n.t("taxonomy")}</span>
                </Tooltip>
            ),
            dataIndex: con.ARC_TAXONOMY_STRING,
            align: 'left',
            width: 200,
            render: (val, record) => {
                if (isNullOrUndefined(val) || val === con.MISSING_VALUE) {
                    return con.MISSING_VALUE;
                }
                return (
                    <span style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        display: 'block',
                        lineHeight: '1.2em',
                        maxHeight: '3.6em',
                        overflow: 'hidden'
                    }}>
                        {val}
                    </span>
                );
            }
        },
        {
            title: (
                <Tooltip title={i18n.t("detailsExplanation")}>
                    <span data-tour="details-column">{i18n.t("details")}</span>
                </Tooltip>
            ),
            align: 'center',
            render: (val, record) => <CustomGoButton onClick={() => {
                setStoredValue(con.SELECTED_ANNOTATION_QUERY_ID, record[con.ARC_QUERY_ID])
                setPage(con.PAGE_ALIGNMENT_RESULTS)

            }} />,
            width: 90,

        }
    ]


    return (
        <div>
            <Row style={{ marginTop: "0.3vh", padding: "0 2vh" }}>
                <Col span={1}>
                    <div data-tour="back-button">
                        <BackButton specificPage={con.PAGE_HOME} />
                    </div>
                </Col>
                <Col span={19}>
                    <Title level={3} style={{
                        marginTop: "1vh",
                        paddingTop: 0,
                        textAlign: 'center',
                        marginBottom: "2.5vh"
                    }}>{i18n.t("annotationResults")}</Title>
                </Col>
                <Col span={2} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: '5px' }}>
                    <div data-tour="download-results">
                        <CustomDownloadButton
                            onClick={handleDownload}
                            loading={downloadLoading}
                            disabled={isNullOrUndefined(data) || data.length === 0}
                        >
                            {i18n.t("downloadResults") || "Download Results"}
                        </CustomDownloadButton>
                    </div>
                </Col>
                <Col span={2} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: '50px' }}>
                    <Tooltip
                        title={i18n.t("bestMatchInfo")}
                        placement="bottomLeft"
                    >
                        <InfoCircleOutlined
                            style={{
                                fontSize: "24px",
                                color: "#1890ff",
                                cursor: "help"
                            }}
                        />
                    </Tooltip>
                </Col>
            </Row>
            <div style={{ paddingLeft: "1vw", paddingRight: "1vw" }}>

                <div data-tour="results-table">
                    <Table
                        dataSource={tableData}
                        columns={GENERAL_COLUMNS}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        pagination={{ pageSize: 15 }}
                        rowKey={con.ARC_QUERY_ID}
                    />
                </div>
            </div>
        </div>
    )
}

export default AnnotationResultsPage
